/**
 * The training screens: the run, the store, and the instruments.
 *
 * Two things live here. `TrainingRuntime` owns everything the menu model is not
 * allowed to touch — a `Trainer`, a Web Worker backend, an IndexedDB store, and
 * whatever `LearnedPolicy` is currently driving cars. The markup builders below
 * it turn that state into HTML for `src/ui/menu.ts` to drop into the panel.
 * Neither decides anything about navigation; that is `MenuModel`'s job, and the
 * split is what keeps the state machine testable in Node with no worker, no
 * database and no trainer at all.
 *
 * ===========================================================================
 * THE CHARTS ARE SVG, DRAWN FROM ARITHMETIC THAT IS TESTED SEPARATELY
 * ===========================================================================
 *
 * No chart library, no canvas, no assets: a polyline in an SVG with a viewBox
 * is a string, it scales without a resize observer, and it costs nothing to
 * rebuild once an iteration. Every number in that string comes from
 * ./training-model, which is pure and covered numerically — because a chart
 * that is subtly wrong looks exactly like a chart that is right, and the only
 * way to know is to check the geometry rather than the picture.
 *
 * ===========================================================================
 * HOW A TRAINED POLICY REACHES A MATCH
 * ===========================================================================
 *
 * `activeCheckpoint` is the single fact that unlocks the `learned` tier. It is
 * set by loading a checkpoint, by importing one, or by an iteration completing;
 * it is cleared when the last checkpoint is deleted. `learnedPolicyFactory`
 * turns it into one `Policy` per bot-driven car, which `botRosterFor` puts on
 * `BotConfig.policy` — the seam src/bot/difficulty.ts describes, used exactly
 * as described. Nothing weakens the guards: with no checkpoint there is no
 * factory, and with no factory the tier resolves back to a scripted one.
 *
 * WATCHING IT LEARN. A watch-live match must show the CURRENT policy, and the
 * current policy changes every iteration — but rebuilding the match each time
 * would mean a new WebGL context every few seconds, and a browser allows only a
 * handful before it starts dropping the oldest. So the policies handed to a
 * watch session are `RefreshablePolicy` wrappers whose inner network is swapped
 * in place. The match keeps running; the driver gets better.
 */

import type { Policy, PolicyContext } from '../bot/bot';
import type { CarInput } from '../physics/car-state';
import { OPPONENT_CHOICES, type GameSettings, type TrainingOpponent } from '../game/settings';
import { createBrowserBackend, type BrowserBackend } from '../rl/browser-backend';
import { LearnedPolicy } from '../rl/policy';
import { createIndexedDbStore, type CheckpointStore } from '../rl/storage';
import { Trainer, type OpponentConfig, type TrainerConfig } from '../rl/trainer';
import type { Checkpoint, TrainerPhase, TrainerProgress } from '../rl/types';
import {
  CHART_SPECS,
  buildChart,
  checkpointRowLabel,
  emptyTrainingStats,
  formatCount,
  formatElapsed,
  formatMetric,
  linePath,
  parseCheckpointFile,
  project,
  rewardBars,
  serializeCheckpoint,
  checkpointFilename,
  trainingConfigFrom,
  type ChartView,
  type CheckpointRow,
  type PlotBox,
} from './training-model';

// ---------------------------------------------------------------------------
// A policy whose network can be replaced under a running match
// ---------------------------------------------------------------------------

/**
 * `Policy` indirection, one per car.
 *
 * `BotDirector` reads its slots' policies once, at construction, and there is
 * no setter — nor should there be, for a class whose whole contract is that a
 * match is reproducible from its configuration. This wrapper is the honest way
 * to get a live swap: the director's slot never changes, and what changes is
 * something this file owns.
 */
class RefreshablePolicy implements Policy {
  readonly name = 'learned';
  private inner: Policy;

  constructor(policy: Policy) {
    this.inner = policy;
  }

  replace(policy: Policy): void {
    this.inner = policy;
    // The new network has no memory of the play in progress, and the old one's
    // held action would otherwise survive the swap.
    this.inner.reset();
  }

  observe(context: PolicyContext): CarInput {
    return this.inner.observe(context);
  }

  reset(): void {
    this.inner.reset();
  }
}

// ---------------------------------------------------------------------------
// Runtime
// ---------------------------------------------------------------------------

export interface TrainingHost {
  /** Something the training screens display has changed; re-render. */
  changed(): void;
  /** One line under the title. Used for outcomes and for failures alike. */
  toast(message: string): void;
}

/** Everything the training panel draws, gathered in one object so the markup
 *  builders stay pure functions of it. */
export interface TrainingView {
  progress: TrainerProgress;
  stepsPerIteration: number;
  /** False when the Web Worker path was unavailable and the fallback is in use. */
  parallel: boolean;
  chunks: number;
  /** Seconds the last iteration spent collecting, off this thread. */
  collectSeconds: number;
  /**
   * Seconds the last iteration spent NOT collecting — the PPO update, which
   * runs here and blocks. Measured rather than described, because it is the
   * number that decides whether the page is usable and it is the one number a
   * frame counter cannot show you (there are no frames while it runs).
   */
  updateSeconds: number;
  /** Label of the checkpoint currently backing the `learned` tier. */
  activeLabel: string | null;
  watching: boolean;
}

const idleProgress = (): TrainerProgress => ({
  phase: 'idle',
  stats: emptyTrainingStats(),
  iterationProgress: 0,
  stepsPerSecond: 0,
});

/**
 * How a menu opponent choice reaches the trainer.
 *
 * `OPPONENT_CHOICES` offers exactly the values `OpponentKind` can express, so
 * this is total with no fallback branch — a menu value with no trainer meaning
 * is precisely what the `implemented` flag exists to keep off the screen.
 */
const opponentConfig = (opponent: TrainingOpponent): OpponentConfig => {
  switch (opponent) {
    case 'self':
      return { kind: 'self' };
    case 'pool':
      return { kind: 'pool' };
    default:
      return { kind: 'scripted', tier: opponent };
  }
};

export class TrainingRuntime {
  private trainer: Trainer | null = null;
  private backend: BrowserBackend | null = null;
  private store: CheckpointStore | null = null;
  private storeBroken = false;

  private progress: TrainerProgress = idleProgress();
  private rows: CheckpointRow[] = [];

  /** The checkpoint the `learned` tier plays from, or null. */
  private activeCheckpoint: Checkpoint | null = null;
  private activeLabel: string | null = null;

  /** Live wrappers handed to a watch session, refreshed each iteration. */
  private watchPolicies: RefreshablePolicy[] = [];

  /** Set once the newest stored checkpoint has been adopted, or has failed to
   *  be. Without it a store whose newest entry cannot be read would be retried
   *  on every visit to the training screen, forever. */
  private autoAdoptSettled = false;

  /** Monotonic suffix, so two saves in the same millisecond get distinct ids. */
  private saveCounter = 0;

  /** Wall clock at the previous iteration's progress callback, and the split
   *  derived from it. See `TrainingView.updateSeconds`. */
  private lastProgressAt = 0;
  private collectStartedAt = 0;
  private collectSeconds = 0;
  private updateSeconds = 0;

  constructor(
    private readonly settings: GameSettings,
    private readonly host: TrainingHost
  ) {}

  // -------------------------------------------------------------------------
  // Read side
  // -------------------------------------------------------------------------

  get phase(): TrainerPhase {
    return this.progress.phase;
  }

  get iteration(): number {
    return this.progress.stats.iteration;
  }

  get checkpoints(): readonly CheckpointRow[] {
    return this.rows;
  }

  /** THE flag that unlocks the `learned` tier. */
  get learnedAvailable(): boolean {
    return this.activeCheckpoint !== null;
  }

  view(): TrainingView {
    return {
      progress: this.progress,
      stepsPerIteration: this.settings.training.stepsPerIteration,
      parallel: this.backend?.parallel ?? true,
      chunks: this.backend?.chunks ?? this.settings.training.workers,
      collectSeconds: this.collectSeconds,
      updateSeconds: this.updateSeconds,
      activeLabel: this.activeLabel,
      watching: this.watchPolicies.length > 0,
    };
  }

  // -------------------------------------------------------------------------
  // Policies
  // -------------------------------------------------------------------------

  /**
   * A factory for `botRosterFor`, or undefined when nothing is trained.
   *
   * Undefined rather than a factory that returns null: the absence of a factory
   * is what makes `resolveDifficulty` fall back to a scripted tier, and a
   * factory that could fail would push that decision into `BotDirector`, which
   * answers it by throwing.
   */
  learnedPolicyFactory(refreshable: boolean): ((carIndex: number) => Policy) | undefined {
    const checkpoint = this.activeCheckpoint;
    if (checkpoint === null) return undefined;
    return () => {
      // A seed per car would be better still, but `LearnedPolicy` derives its
      // own from the checkpoint; what matters here is that each car gets its
      // own instance so `reset` does not cross wires.
      const policy = new LearnedPolicy(checkpoint);
      if (!refreshable) return policy;
      const wrapper = new RefreshablePolicy(policy);
      this.watchPolicies.push(wrapper);
      return wrapper;
    };
  }

  /** Called when a watch session is torn down. */
  releaseWatchPolicies(): void {
    this.watchPolicies = [];
  }

  // -------------------------------------------------------------------------
  // The run
  // -------------------------------------------------------------------------

  start(): void {
    if (this.trainer !== null) return;
    const training = this.settings.training;
    const checkpoint = this.activeCheckpoint;
    const config: TrainerConfig = {
      ...trainingConfigFrom(training),
      opponent: opponentConfig(training.opponent),
      // Trainer-side, like the opponent: `TrainingConfig` is the contract every
      // part of the stack shares and deliberately does not carry it.
      resetMode: training.resetMode,
      // A resumed run must use the SHAPE it was trained at; `Trainer` refuses
      // the mismatch rather than reinterpreting the weights, which is right.
      ...(checkpoint === null ? {} : { hiddenSizes: [...checkpoint.hiddenSizes] }),
    };

    try {
      this.backend = createBrowserBackend(config, training.workers);
      // The clock lives here rather than in the backend: nothing under src/rl
      // may read one. See `BrowserBackend.onCollectSpan`.
      this.backend.onCollectSpan = edge => {
        if (edge === 'start') this.collectStartedAt = clock();
        else this.collectSeconds = (clock() - this.collectStartedAt) / 1000;
      };
      this.trainer = new Trainer(config, {
        backend: this.backend,
        ...(checkpoint === null ? {} : { checkpoint }),
        onProgress: progress => this.onProgress(progress),
      });
    } catch (error) {
      this.teardownRun();
      this.host.toast(`Training could not start: ${message(error)}`);
      this.host.changed();
      return;
    }

    if (!this.backend.parallel) {
      this.host.toast('Web Workers are unavailable; rollouts run on this thread and it will stutter.');
    }

    const trainer = this.trainer;
    this.progress = trainer.progress;
    // Not awaited: `run` resolves only when the run ends, and the whole point
    // is that the menu keeps taking input while it does not.
    void trainer
      .run()
      .catch(error => this.host.toast(`Training stopped: ${message(error)}`))
      .finally(() => {
        this.progress = { ...trainer.progress, phase: trainer.phase };
        this.host.changed();
      });
    this.host.changed();
  }

  pause(): void {
    this.trainer?.pause();
    this.progress = this.trainer?.progress ?? this.progress;
    this.host.changed();
  }

  resume(): void {
    this.trainer?.resume();
    this.progress = this.trainer?.progress ?? this.progress;
    this.host.changed();
  }

  stop(): void {
    const trainer = this.trainer;
    if (trainer === null) return;
    trainer.stop();
    // The final statistics are kept: a run that has been stopped is still the
    // run whose charts the player is looking at, and blanking them on stop
    // would throw away the only record of what happened.
    this.progress = { ...trainer.progress, phase: 'stopped' };
    this.teardownRun();
    this.host.changed();
  }

  private teardownRun(): void {
    this.backend?.dispose();
    this.backend = null;
    this.trainer?.dispose();
    this.trainer = null;
    this.lastProgressAt = 0;
  }

  /**
   * One iteration finished.
   *
   * The checkpoint is snapshotted every iteration, not only on save: it is what
   * the `learned` tier plays from and what the watch session is refreshed with,
   * and `toCheckpoint` copies every array so taking one while training
   * continues is safe.
   */
  private onProgress(progress: TrainerProgress): void {
    this.progress = progress;

    const at = clock();
    const period = this.lastProgressAt === 0 ? 0 : (at - this.lastProgressAt) / 1000;
    this.lastProgressAt = at;
    // Whatever the iteration took that the workers did not. The first iteration
    // has no previous callback to measure from and reports zero rather than a
    // number that includes the workers being built.
    this.updateSeconds = Math.max(0, period - this.collectSeconds);

    const trainer = this.trainer;
    if (trainer !== null) {
      this.activeCheckpoint = trainer.toCheckpoint(`live · i${progress.stats.iteration}`);
      this.activeLabel = this.activeCheckpoint.label ?? null;
      for (const wrapper of this.watchPolicies) {
        wrapper.replace(new LearnedPolicy(this.activeCheckpoint));
      }
    }
    this.host.changed();
  }

  // -------------------------------------------------------------------------
  // The checkpoint store
  // -------------------------------------------------------------------------

  /** Lazily opened, and only once: a browser with IndexedDB disabled throws on
   *  every call, and retrying it per keystroke would be a stream of failures. */
  private checkpointStore(): CheckpointStore | null {
    if (this.storeBroken) return null;
    if (this.store === null) {
      try {
        this.store = createIndexedDbStore();
      } catch {
        this.storeBroken = true;
        return null;
      }
    }
    return this.store;
  }

  refresh(): void {
    const store = this.checkpointStore();
    if (store === null) {
      this.rows = [];
      return;
    }
    void store
      .list()
      .then(metas => {
        this.rows = metas.map(meta => ({
          id: meta.id,
          label: meta.label ?? meta.id,
          iteration: meta.iteration,
          totalSteps: meta.totalSteps,
          savedAt: meta.savedAt,
        }));
        this.host.changed();
        this.autoAdopt();
      })
      .catch(error => {
        this.storeBroken = true;
        this.host.toast(`Checkpoint storage unavailable: ${message(error)}`);
        this.host.changed();
      });
  }

  /**
   * Drop the in-memory policy when a DELETE has emptied the store.
   *
   * Deleting the last checkpoint must relock the `learned` tier, and without
   * this it would not: the policy that was loaded is still in memory, still
   * plays, and would keep the tier selectable after the thing it came from was
   * thrown away. "A checkpoint is available" would then mean two different
   * things in two places, which is exactly the divergence the tier's three
   * refusals exist to prevent.
   *
   * A RUNNING trainer is the exception, and not one: its network is genuinely
   * available, it re-snapshots every iteration, and it is not what the player
   * just deleted.
   */
  private forgetIfStoreEmpty(): void {
    if (this.rows.length > 0 || this.trainer !== null) return;
    this.activeCheckpoint = null;
    this.activeLabel = null;
    this.autoAdoptSettled = false;
  }

  /**
   * Adopt the newest stored checkpoint at boot.
   *
   * "A compatible checkpoint is available" is what unlocks the `learned` tier,
   * and a checkpoint sitting in IndexedDB from a run last week is available. A
   * player who has trained a bot should not have to walk into a sub-screen and
   * press Load before the tier they trained appears — that is the tier being
   * locked for a reason that is not the stated one.
   *
   * The list is newest first (see ../rl/storage), so `rows[0]` is the one. It
   * is still validated: adopting is what makes the tier selectable, and an
   * unreadable file must not do that.
   */
  private autoAdopt(): void {
    if (this.autoAdoptSettled || this.activeCheckpoint !== null || this.rows.length === 0) return;
    const store = this.checkpointStore();
    if (store === null) return;
    this.autoAdoptSettled = true;
    void store
      .load(this.rows[0].id)
      .then(checkpoint => {
        const check = parseCheckpointFile(JSON.stringify(checkpoint));
        if (!check.ok) return;
        this.adopt(check.checkpoint);
        this.host.changed();
      })
      .catch(() => {
        /* the row stays; loading it by hand will say why */
      });
  }

  save(): void {
    const trainer = this.trainer;
    const checkpoint = trainer !== null ? trainer.toCheckpoint() : this.activeCheckpoint;
    if (checkpoint === null) {
      this.host.toast('Nothing to save yet.');
      return;
    }
    const store = this.checkpointStore();
    if (store === null) {
      this.host.toast('Checkpoint storage is unavailable in this browser.');
      return;
    }

    const iteration = checkpoint.stats.iteration;
    // `assertId` in ../rl/storage accepts letters, digits, dot, dash and
    // underscore only, so nothing here may be interpolated raw.
    const id = `run-${iteration}-${Date.now()}-${this.saveCounter++}`;
    // Named for what it was trained AGAINST, not for how far it got: the
    // iteration, the step count and the date are already on the row, and a
    // label that repeats them tells the player nothing when two are side by
    // side.
    const opponent =
      OPPONENT_CHOICES.find(c => c.value === this.settings.training.opponent)?.label ??
      this.settings.training.opponent;
    const labelled: Checkpoint = { ...checkpoint, label: opponent };
    void store
      .save(id, labelled)
      .then(() => {
        this.adopt(labelled);
        this.host.toast(`Saved: ${labelled.label} · i${iteration}.`);
        this.refresh();
      })
      .catch(error => {
        this.host.toast(`Save failed: ${message(error)}`);
        this.host.changed();
      });
  }

  load(id: string): void {
    const store = this.checkpointStore();
    if (store === null) return;
    void store
      .load(id)
      .then(checkpoint => {
        const check = parseCheckpointFile(JSON.stringify(checkpoint));
        if (!check.ok) {
          this.host.toast(check.message);
          this.host.changed();
          return;
        }
        this.adopt(check.checkpoint);
        this.host.toast('Loaded. Learned (RL) is now selectable in Match Setup.');
        this.host.changed();
      })
      .catch(error => {
        this.host.toast(`Load failed: ${message(error)}`);
        this.host.changed();
      });
  }

  remove(id: string): void {
    const store = this.checkpointStore();
    if (store === null) return;
    void store
      .delete(id)
      .then(() => {
        this.host.toast('Deleted.');
        // Dropped locally first, so "was that the last one?" is answered
        // before the asynchronous re-read comes back. `refresh` is generic and
        // must not relock the tier on its own — a stopped run's policy is
        // still in memory, still works, and was never in the store.
        this.rows = this.rows.filter(row => row.id !== id);
        this.forgetIfStoreEmpty();
        this.refresh();
      })
      .catch(error => {
        this.host.toast(`Delete failed: ${message(error)}`);
        this.host.changed();
      });
  }

  /** Download a stored checkpoint as JSON. */
  exportFile(id: string): void {
    const store = this.checkpointStore();
    if (store === null) return;
    void store
      .load(id)
      .then(checkpoint => download(serializeCheckpoint(checkpoint), checkpointFilename(checkpoint)))
      .catch(error => {
        this.host.toast(`Export failed: ${message(error)}`);
        this.host.changed();
      });
  }

  /**
   * Import from a file picker.
   *
   * Everything about the file is suspect — see the header of ./training-model.
   * A rejection is reported as a sentence and nothing is adopted; there is no
   * partial import, because a partially loaded network is a network that plays
   * like noise while claiming to be trained.
   */
  importFile(): void {
    if (typeof document === 'undefined') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file
        .text()
        .then(text => {
          const check = parseCheckpointFile(text);
          if (!check.ok) {
            this.host.toast(check.message);
            this.host.changed();
            return;
          }
          this.adopt(check.checkpoint);
          const store = this.checkpointStore();
          if (store === null) {
            this.host.toast('Imported for this session; checkpoint storage is unavailable.');
            this.host.changed();
            return;
          }
          const id = `import-${Date.now()}-${this.saveCounter++}`;
          void store
            .save(id, { ...check.checkpoint, label: check.checkpoint.label ?? 'imported' })
            .then(() => {
              this.host.toast('Imported. Learned (RL) is now selectable in Match Setup.');
              this.refresh();
            })
            .catch(error => {
              this.host.toast(`Imported, but not stored: ${message(error)}`);
              this.host.changed();
            });
        })
        .catch(error => {
          this.host.toast(`Could not read that file: ${message(error)}`);
          this.host.changed();
        });
    });
    input.click();
  }

  /** Make a checkpoint the live policy, and refresh anything watching. */
  private adopt(checkpoint: Checkpoint): void {
    this.activeCheckpoint = checkpoint;
    this.activeLabel = checkpoint.label ?? `i${checkpoint.stats.iteration}`;
    for (const wrapper of this.watchPolicies) {
      wrapper.replace(new LearnedPolicy(checkpoint));
    }
  }

  dispose(): void {
    this.teardownRun();
    this.watchPolicies = [];
  }
}

/** The one wall-clock read in the training front end, for the two instruments
 *  that report how long an iteration's halves took. Nothing a run does depends
 *  on it. */
const clock = (): number =>
  typeof performance === 'undefined' ? Date.now() : performance.now();

const message = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Save a string to the user's downloads. An anchor with `download` is the one
 * form that works without a permission prompt and without a library; the object
 * URL is revoked on the next task so the click has already been dispatched.
 */
const download = (text: string, filename: string): void => {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// ---------------------------------------------------------------------------
// Markup
// ---------------------------------------------------------------------------

const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Chart viewBox. Fixed user units; the SVG scales to whatever the grid gives
 *  it, which is why nothing here needs a resize observer. */
const BOX: PlotBox = { width: 240, height: 78, pad: 5 };

/** One line per series. Two is the most any spec has, and a third colour that
 *  is not the accent would start competing with the focus bar. */
const LINE_CLASSES = ['a', 'b'] as const;

const chartMarkup = (view: ChartView): string => {
  const paths = view.lines
    .map((line, i) => {
      const d = linePath(line, view, BOX);
      if (d === '') return '';
      const cls = LINE_CLASSES[Math.min(i, LINE_CLASSES.length - 1)];
      return `<path class="rw-spark ${cls}" d="${d}" />`;
    })
    .join('');

  // A zero rule, but only when zero is inside the range — a rule pinned to the
  // bottom edge of a range that never reaches zero is a lie about the baseline.
  const zero =
    view.y.min <= 0 && view.y.max >= 0
      ? `<line class="rw-zero" x1="${BOX.pad}" x2="${BOX.width - BOX.pad}" y1="${project(
          { x: view.x.min, y: 0 },
          view,
          BOX
        ).y.toFixed(2)}" y2="${project({ x: view.x.min, y: 0 }, view, BOX).y.toFixed(2)}" />`
      : '';

  const readouts = view.lines
    .map((line, i) => {
      const cls = LINE_CLASSES[Math.min(i, LINE_CLASSES.length - 1)];
      const value =
        line.last === null
          ? '—'
          : view.percent
            ? `${(line.last * 100).toFixed(0)}%`
            : formatMetric(line.last, view.decimals);
      return `<span class="rw-read ${cls}">${esc(value)}</span>`;
    })
    .join('');

  const axis = `${formatMetric(view.y.min, view.decimals)} … ${formatMetric(view.y.max, view.decimals)}`;

  return (
    `<div class="rw-chart">` +
    `<div class="rw-chart-head"><span class="rw-chart-name">${esc(view.label)}</span>${readouts}</div>` +
    `<svg class="rw-chart-svg" viewBox="0 0 ${BOX.width} ${BOX.height}" preserveAspectRatio="none" aria-hidden="true">` +
    zero +
    paths +
    `</svg>` +
    `<div class="rw-chart-axis">${esc(axis)}</div>` +
    `</div>`
  );
};

const statCell = (key: string, value: string): string =>
  `<div class="rw-stat"><div class="rw-stat-key">${esc(key)}</div>` +
  `<div class="rw-stat-val">${esc(value)}</div></div>`;

const PHASE_LABELS: Record<TrainerPhase, string> = {
  idle: 'Idle',
  collecting: 'Collecting',
  updating: 'Updating',
  paused: 'Paused',
  stopped: 'Stopped',
};

/** The training screen's whole instrument panel. */
export const trainingPanelMarkup = (view: TrainingView): string => {
  const stats = view.progress.stats;
  const latest = stats.history[stats.history.length - 1];

  const bar = Math.min(
    1,
    Math.max(0, view.stepsPerIteration > 0 ? view.progress.iterationProgress / view.stepsPerIteration : 0)
  );

  const strip =
    `<div class="rw-stats">` +
    statCell('Phase', PHASE_LABELS[view.progress.phase]) +
    statCell('Iteration', `${stats.iteration}`) +
    statCell('Steps', formatCount(stats.totalSteps)) +
    statCell('Steps/s', formatCount(Math.round(view.progress.stepsPerSecond))) +
    statCell('Elapsed', formatElapsed(stats.totalSeconds)) +
    // Difficulty AND iterations since it last rose. The second number is the
    // guard: a curriculum that never advances is otherwise a value that simply
    // sits there, and nothing on this screen would say it was stuck.
    statCell(
      'Curriculum',
      latest?.difficulty === undefined
        ? '—'
        : `${latest.difficulty.toFixed(2)} · +${latest.curriculumStall ?? 0}`
    ) +
    statCell(
      'Rollout',
      view.parallel
        ? `${view.collectSeconds.toFixed(1)}s · ${view.chunks}w`
        : `${view.collectSeconds.toFixed(1)}s · on thread`
    ) +
    // Named for what it costs the player, not for what it is. This is the
    // number that says how long the page is frozen every iteration.
    statCell('UI Freeze', `${view.updateSeconds.toFixed(1)}s`) +
    `</div>` +
    `<div class="rw-progress"><i style="width:${(bar * 100).toFixed(1)}%"></i></div>`;

  const charts = CHART_SPECS.map(spec => chartMarkup(buildChart(spec, stats.history))).join('');

  const bars = rewardBars(latest?.rewardTerms);
  const breakdown =
    bars.length === 0
      ? ''
      : `<div class="rw-terms"><div class="rw-terms-head">Reward Terms · last iteration</div>` +
        bars
          .map(
            term =>
              `<div class="rw-term"><span class="rw-term-name">${esc(term.name)}</span>` +
              `<span class="rw-term-bar${term.negative ? ' neg' : ''}">` +
              `<i style="width:${(term.fraction * 100).toFixed(1)}%"></i></span>` +
              `<span class="rw-term-val">${esc(term.value.toFixed(3))}</span></div>`
          )
          .join('') +
        `</div>`;

  const active =
    view.activeLabel === null
      ? `<div class="rw-active none">No policy loaded — Learned (RL) stays locked in Match Setup.</div>`
      : `<div class="rw-active">Active policy: <b>${esc(view.activeLabel)}</b>${
          view.watching ? ' · driving the match behind this menu' : ''
        }</div>`;

  return `${strip}<div class="rw-charts">${charts}</div>${breakdown}${active}`;
};

/** The checkpoint list screen's header block. */
export const checkpointsPanelMarkup = (rows: readonly CheckpointRow[]): string =>
  rows.length === 0
    ? `<div class="rw-active none">Nothing saved yet. Train a few iterations, then save.</div>`
    : `<div class="rw-active">${rows.length} checkpoint${rows.length === 1 ? '' : 's'} in this browser's storage.</div>`;

/** The detail screen's header block. */
export const checkpointDetailMarkup = (row: CheckpointRow | null): string =>
  row === null
    ? `<div class="rw-active none">That checkpoint is gone.</div>`
    : `<div class="rw-stats">` +
      statCell('Label', row.label) +
      statCell('Iteration', `${row.iteration}`) +
      statCell('Steps', formatCount(row.totalSteps)) +
      `</div><div class="rw-active">${esc(checkpointRowLabel(row))}</div>`;
